import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, FileUp, X } from "lucide-react";
import { toast } from "sonner";
import { REQUIREMENT_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.dxf,.dwg,.step,.stp";

const inputCls =
  "w-full border border-slate-300 rounded-sm px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/40 focus:border-[#2563EB] transition";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5";

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const RfqForm = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    requirement: "",
    product: "",
    material: "",
    quantity: "",
    message: "",
  });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [reference, setReference] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    const product = params.get("product");
    const requirement = params.get("requirement");
    if (product || requirement) {
      setForm((f) => ({
        ...f,
        product: product || f.product,
        requirement: requirement && REQUIREMENT_OPTIONS.includes(requirement) ? requirement : f.requirement,
      }));
    }
  }, [params]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("drawing", file);
      const res = await axios.post(`${API}/enquiries`, fd);
      setReference(res.data.reference_id);
      setStatus("success");
      toast.success("Enquiry submitted successfully.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (status === "success") {
    return (
      <div data-testid="rfq-success-message" className="border border-slate-200 bg-white p-10 text-center rounded-sm">
        <CheckCircle2 size={44} className="mx-auto text-green-700" />
        <h3 className="mt-5 text-xl font-semibold text-slate-900">Enquiry Received</h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
          Thank you. Your enquiry has been received. Our team will review your requirement and get back to you.
        </p>
        <p className="mt-4 text-xs font-mono text-slate-500">
          Reference ID: <span className="font-semibold text-slate-800">{reference}</span>
        </p>
        <button
          type="button"
          data-testid="rfq-another-button"
          onClick={() => {
            setStatus("idle");
            setFile(null);
            setForm({ name: "", company: "", email: "", phone: "", requirement: "", product: "", material: "", quantity: "", message: "" });
          }}
          className="mt-6 text-sm font-semibold text-[#2563EB] hover:text-[#1D4ED8]"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="rfq-form"
      onSubmit={onSubmit}
      className="border border-slate-200 bg-white p-6 sm:p-8 rounded-sm"
    >
      <h3 className="text-xl font-semibold text-slate-900">Request a Quote</h3>
      <p className="mt-2 text-sm text-slate-600">
        Share your requirement and attach a drawing if available. Fields marked * are required.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rfq-name" className={labelCls}>Name *</label>
          <input id="rfq-name" data-testid="rfq-form-name-input" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your full name" />
        </div>
        <div>
          <label htmlFor="rfq-company" className={labelCls}>Company Name *</label>
          <input id="rfq-company" data-testid="rfq-form-company-input" required value={form.company} onChange={update("company")} className={inputCls} placeholder="Company / organisation" />
        </div>
        <div>
          <label htmlFor="rfq-email" className={labelCls}>Email *</label>
          <input id="rfq-email" type="email" data-testid="rfq-form-email-input" required value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" />
        </div>
        <div>
          <label htmlFor="rfq-phone" className={labelCls}>Phone *</label>
          <input id="rfq-phone" type="tel" data-testid="rfq-form-phone-input" required value={form.phone} onChange={update("phone")} className={inputCls} placeholder="Contact number" />
        </div>
        <div>
          <label htmlFor="rfq-requirement" className={labelCls}>Requirement *</label>
          <select id="rfq-requirement" data-testid="rfq-form-requirement-select" required value={form.requirement} onChange={update("requirement")} className={inputCls}>
            <option value="" disabled>Select requirement</option>
            {REQUIREMENT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rfq-product" className={labelCls}>Product / Component</label>
          <input id="rfq-product" data-testid="rfq-form-product-input" value={form.product} onChange={update("product")} className={inputCls} placeholder="e.g. Gasket Die, Conveyor Roller" />
        </div>
        <div>
          <label htmlFor="rfq-material" className={labelCls}>Material</label>
          <input id="rfq-material" data-testid="rfq-form-material-input" value={form.material} onChange={update("material")} className={inputCls} placeholder="e.g. EN8, SS304, EN31" />
        </div>
        <div>
          <label htmlFor="rfq-quantity" className={labelCls}>Quantity</label>
          <input id="rfq-quantity" data-testid="rfq-form-quantity-input" value={form.quantity} onChange={update("quantity")} className={inputCls} placeholder="e.g. 10 nos" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="rfq-message" className={labelCls}>Message / Technical Details</label>
          <textarea id="rfq-message" data-testid="rfq-form-message-input" rows={4} value={form.message} onChange={update("message")} className={inputCls} placeholder="Dimensions, tolerances, surface finish, application details" />
        </div>
        <div className="sm:col-span-2">
          <span className={labelCls}>Upload Drawing / File</span>
          <input
            ref={fileRef}
            id="rfq-file"
            type="file"
            accept={ACCEPT}
            data-testid="rfq-form-file-upload"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <div className="flex items-center justify-between border border-slate-300 bg-slate-50 rounded-sm px-4 py-3">
              <span data-testid="rfq-file-name" className="text-sm text-slate-700 truncate">
                {file.name} <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
              </span>
              <button
                type="button"
                data-testid="rfq-file-remove"
                onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                className="ml-3 text-slate-500 hover:text-slate-800"
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <label
              htmlFor="rfq-file"
              data-testid="rfq-file-dropzone"
              className="flex flex-col items-center justify-center gap-2 border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 rounded-sm px-4 py-8 cursor-pointer transition-colors"
            >
              <FileUp size={22} className="text-[#2563EB]" />
              <span className="text-sm font-medium text-slate-700">Click to upload a drawing</span>
              <span className="text-xs text-slate-500">PDF, JPG, PNG, DXF, DWG, STEP, STP — up to 20 MB</span>
            </label>
          )}
        </div>
      </div>

      <button
        type="submit"
        data-testid="rfq-form-submit-button"
        disabled={status === "submitting"}
        className="mt-6 w-full sm:w-auto inline-flex justify-center bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white text-sm font-semibold px-8 py-3 rounded-sm transition-colors duration-150"
      >
        {status === "submitting" ? "Submitting..." : "Submit Enquiry"}
      </button>
    </form>
  );
};

export default RfqForm;
